import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../api/queryKeys";
import { useMyDetails } from "../../api/users/useMyDetails";
import { useParams } from "react-router-dom";
import { useKeycloak } from '@react-keycloak/web';
import OrganizationDetails from "./OrganizationDetails";
import { getOrganizationDetails, getOrganizationMembers, removeOrganizationMember } from "../../api/organizations/organizationsApi";
import { useNavigate } from 'react-router-dom';
import { getUserDetails } from "../../api/users/usersApi";
import { toast } from 'react-toastify';
import { Member, Organization, buildMember, buildOrganization } from "../common/ObjectTypes";
import { ManageOrganizationModal } from "./ManageOrganizationModal";

import RemoveMemberModal from "./RemoveMemberModal";
import { ManageMemberModal } from "../users/ManageMemberModal";
import { useTranslation } from "react-i18next";
import NotFoundPage from "../common/NotFoundPage";
import { page_size } from "../../api/apiUtils";

const EMPTY_ORGANIZATION: Organization = buildOrganization();
const EMPTY_MEMBERS: Member[] = [];
const EMPTY_MEMBER: Member = buildMember();

export function ManageOrganizationDetails() {
    const { org_id } = useParams();
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const navigate = useNavigate()
    const [offset, setOffset] = useState(0);
    // The workspace and its members. Every workspace or member save refreshes
    // this through the API layer, so nothing here has to ask for a reload.
    const orgQuery = useQuery({
        queryKey: queryKeys.organizations.detail(org_id ?? ""),
        enabled: !!keycloak.token && !keycloak.isTokenExpired() && !!org_id,
        queryFn: async () => {
            const [tempMembers, tempDetails] = await Promise.all([
                getOrganizationMembers(keycloak.token as string, org_id as string),
                getOrganizationDetails(keycloak.token as string, org_id as string),
            ]);
            // A swallowed 401 resolves undefined (the session-expired modal
            // owns that case); rendering would crash on organization.name,
            // so keep the loading state instead.
            if (!tempDetails) return null;
            return { organization: tempDetails as Organization, members: (tempMembers?.results ?? []) as Member[] };
        },
    });
    const organization: Organization = orgQuery.data?.organization ?? EMPTY_ORGANIZATION;
    const orgMembers: Member[] = orgQuery.data?.members ?? EMPTY_MEMBERS;
    const pageLoaded = !!orgQuery.data;
    // 403 is treated like 404: the entity is not viewable by this user. A
    // refresh that answers this (the workspace was deleted, or you left it)
    // wins over the workspace already on screen.
    const errorStatus = (orgQuery.error as any)?.response?.status;
    const notFound = errorStatus === 404 || errorStatus === 403;
    // Removing members can shrink the list past the current page (e.g.
    // clearing the only entry on the last one).
    useEffect(() => {
        setOffset((prev) => (prev >= orgMembers.length ? 0 : prev));
    }, [orgMembers.length]);
    const myDetails = useMyDetails(); // this is to check superuser status
    const currentMember: Member = useMemo(
        () => (myDetails?.email && orgMembers.find((member: any) => member?.email === myDetails.email)) || EMPTY_MEMBER,
        [myDetails?.email, orgMembers]
    );
    const [updateModal, setUpdateModal] = useState(false);
    const [inviteMemberModal, setInviteMemberModal] = useState(false);
    const [updateMemberModal, setUpdateMemberModal] = useState(false);
    const [removeMemberModal, setRemoveMemberModal] = useState(false);
    const [memberid, setMemberid] = useState("");
    const [memberToUpdate, setMemberToUpdate] = useState<Member>(buildMember())

    const handleBackArrow = () => navigate("/workspaces/")

    // The whole member list is already in memory — the endpoint has no
    // per-member filter, so the current-member lookup above needs all of it —
    // so page the table by slicing rather than by refetching.
    const membersOnPage = orgMembers.slice(offset, offset + page_size);

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
    }

    async function handleRemoveMember(member_id: string) {
        if (keycloak.token && org_id && member_id) {
            const tempMember = await getUserDetails(keycloak.token, org_id, member_id);
            await removeOrganizationMember(keycloak.token, org_id, member_id, tempMember);
            toast.success(`${t("Member removed from workspace")}`, {
                position: toast.POSITION.TOP_RIGHT
            });
            setRemoveMemberModal(false);
        }
    }

    const handleToggleOrgModal = (saved = false) => {
        setUpdateModal(!updateModal);
    }
    const toggleOrgModalVisibility: React.MouseEventHandler<HTMLButtonElement> = () => {
        handleToggleOrgModal()
    }

    const handleToggleInviteMemberModal = (saved = false) => {
        setInviteMemberModal(!inviteMemberModal);
    }

    async function handleToggleUpdateMemberModal(member_id?: string, saved = false) {
        if (keycloak.token && org_id && member_id) {
            const tempMember = await getUserDetails(keycloak.token, org_id, member_id);
            setMemberToUpdate(tempMember);
        }
        setUpdateMemberModal(!updateMemberModal);
    }

    const handleToggleRemoveMemberModal = (member_id: string) => {
        setRemoveMemberModal(!removeMemberModal);
        setMemberid(member_id);
    }

    if (notFound) return <NotFoundPage />;

    return (
            <div>
                {updateModal &&
                    <ManageOrganizationModal
                        onToggleModal={handleToggleOrgModal}
                        existingOrg={organization}
                        newOrg={false}
                    />
                }
                {inviteMemberModal &&
                    <ManageMemberModal
                        onToggleModal={handleToggleInviteMemberModal}
                        existingMember={memberToUpdate}
                        newMember={true}
                        currentMember={currentMember}
                        superuser={!!myDetails?.is_superuser}
                        orgName={organization.name}
                    />
                }
                {updateMemberModal &&
                    <ManageMemberModal
                        onToggleModal={(saved) => handleToggleUpdateMemberModal(undefined, saved)}
                        existingMember={memberToUpdate}
                        newMember={false}
                        currentMember={currentMember}
                        superuser={!!myDetails?.is_superuser}
                        orgName={organization.name}
                    />
                }
                {removeMemberModal &&
                    <RemoveMemberModal
                        onToggleModal={handleToggleRemoveMemberModal}
                        onRemoveMember={handleRemoveMember}
                        memberid={memberid}
                        currentMember={currentMember}
                        superuser={!!myDetails?.is_superuser}
                        orgName={organization.name}
                    />
                }
                <OrganizationDetails
                    orgMembers={membersOnPage}
                    totalCount={orgMembers.length}
                    offset={offset}
                    take={page_size}
                    onPageClicked={handlePageClick}
                    organization={organization}
                    onToggleModal={toggleOrgModalVisibility}
                    onToggleRemoveMemberModal={handleToggleRemoveMemberModal}
                    onBackArrow={handleBackArrow}
                    onClickInvite={() => handleToggleInviteMemberModal()}
                    onClickMember={handleToggleUpdateMemberModal}
                    currentMember={currentMember}
                    superuser={!!myDetails?.is_superuser}
                    pageLoaded={pageLoaded}
                />
            </div>
        )
}